import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DoctorLayout from "../../Layouts/Doctor/DoctorLayout";
import { getAppointments } from "../../Services/doctorService";
import PremiumLoader from "../../Components/Common/Loader/PremiumLoader";
import Pagination from "../../Components/Common/Pagination/Pagination";
import { useMasterData } from "../../Components/Common/commonEvents";


export default function DoctorDashboard() {

    const navigate = useNavigate();
    const [sessionUserData, setSessionUserData] = useState([]);
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [appointmentsCache, setAppointmentsCache] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        const userData = JSON.parse(sessionStorage.getItem("userData"));

        if (userData) {
            setSessionUserData(userData);
        }
    }, []);

    const formatInputDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const getDefaultAppointmentDates = () => {
        const today = new Date();
        const next7Days = new Date();

        next7Days.setDate(today.getDate() + 7);

        return {
            fromDate: formatInputDate(today),
            toDate: formatInputDate(next7Days),
        };
    };

    const defaultDates = getDefaultAppointmentDates();

    const [filters, setFilters] = useState({
        patientName: "",
        fromDate: defaultDates.fromDate,
        toDate: defaultDates.toDate,
        status: "",
    });

    const formatDate = (dateValue) => {
        if (!dateValue) return "";

        return new Date(dateValue).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatTime = (timeValue) => {
        if (!timeValue) return "";

        return new Date(timeValue).toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    };

    const {
        doctorDashboardCounts,

        fetchDoctorDashboardSummary,
    } = useMasterData();

    useEffect(() => {
        if (!sessionUserData?.OrgId && sessionUserData?.Id) return;

        fetchDoctorDashboardSummary(sessionUserData?.OrgId, sessionUserData?.Id);
    }, [sessionUserData?.OrgId, sessionUserData?.Id]);

    useEffect(() => {
        if (!sessionUserData?.OrgId || !sessionUserData?.UnitId) return;

        fetchAppointments(1, pageSize, true);
    }, [sessionUserData?.OrgId, sessionUserData?.UnitId]);

    const mapAppointment = (item) => ({
        ...item,
        patient: item.PatientName,
        age: item.Age || "-",
        gender: item.Gender === 1 ? "Male" : item.Gender === 2 ? "Female" : "-",
        phone: item.Mobile,
        doctor: item.DoctorName || "-",
        date: formatDate(item.AppointmentDate),
        time: formatTime(item.SlotTime),
        status: item.Status,
    });

    const getPatientSearchParams = (value = "") => {
        const search = value.trim();

        if (!search) {
            return {
                PatientName: "",
                PhoneNumber: "",
            };
        }

        const onlyNumbers = /^\d+$/.test(search);

        if (onlyNumbers) {
            return {
                PatientName: "",
                PhoneNumber: search,
            };
        }

        return {
            PatientName: search,
            PhoneNumber: "",
        };
    };

    const buildPayload = (page = currentPage, size = pageSize) => {
        const patientSearch = getPatientSearchParams(filters.patientName);

        return {
            OrgId: Number(sessionUserData?.OrgId),
            UnitId: Number(sessionUserData?.UnitId),
            DoctorId: sessionUserData?.Id,
            Status: filters.status || "",
            FromDate: filters.fromDate || "",
            ToDate: filters.toDate || "",
            ...patientSearch,
            page,
            pageSize: size,
        };
    };

    const getCacheKey = (page, size) => {
        return JSON.stringify({
            ...filters,
            page,
            pageSize: size,
        });
    };

    const fetchAppointments = async (page = currentPage, size = pageSize, force = false) => {
        const cacheKey = getCacheKey(page, size);

        if (!force && appointmentsCache[cacheKey]) {
            const cached = appointmentsCache[cacheKey];

            setAppointments(cached.data);
            setTotalCount(cached.totalCount);
            setCurrentPage(page);
            return;
        }

        try {
            setLoading(true);

            const payload = buildPayload(page, size);
            const response = await getAppointments(payload);

            const mappedData = (response?.data || []).map(mapAppointment);
            const count = response?.pagination?.totalCount || 0;

            setAppointments(mappedData);
            setTotalCount(count);
            setCurrentPage(page);

            setAppointmentsCache((prev) => ({
                ...prev,
                [cacheKey]: {
                    data: mappedData,
                    totalCount: count,
                },
            }));
        } catch (error) {
            console.error("Failed to fetch appointments:", error);
            setAppointments([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const topStats = [
        {
            title: "Lorem ipsum",
            value: 0,
            sub: "Today overview",
            icon: "bi bi-calendar2-check text-white",
            gradient: "linear-gradient(135deg, #1f6feb, #58a6ff)",
        },
        {
            title: "Lorem ipsum",
            value: 0,
            sub: "Active records",
            icon: "bi bi-people text-white",
            gradient: "linear-gradient(135deg, #1f8f5f, #49c98a)",
        },
        {
            title: "Lorem ipsum",
            value: 0,
            sub: "Current queue",
            icon: "bi bi-hourglass-split text-white",
            gradient: "linear-gradient(135deg, #22a6c9, #56ccf2)",
        },
        {
            title: "Lorem ipsum",
            value: 0,
            sub: "Today",
            icon: "bi bi-person-plus text-white",
            gradient: "linear-gradient(135deg, #f7971e, #ffd200)",
        },
    ];

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFilters((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();

        setAppointmentsCache({});
        setCurrentPage(1);
        fetchAppointments(1, pageSize, true);
    };

    const handlePageChange = (page) => {
        fetchAppointments(page, pageSize);
    };

    const handlePageSizeChange = (size) => {
        setPageSize(size);
        setCurrentPage(1);
        fetchAppointments(1, size);
    };

    useEffect(() => {
        fetchAppointments(1, pageSize, true);
    }, []);

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case "new":
                return "badge badge-light-info text-info fw-bold px-3 py-2 rounded-pill";

            case "booked":
                return "badge badge-light-primary text-primary fw-bold px-3 py-2 rounded-pill";

            case "confirmed":
                return "badge badge-light-success text-success fw-bold px-3 py-2 rounded-pill";

            case "completed":
                return "badge badge-light-success text-success fw-bold px-3 py-2 rounded-pill";

            case "draft":
                return "badge badge-light-secondary text-secondary fw-bold px-3 py-2 rounded-pill";

            case "rejected":
                return "badge badge-light-danger text-danger fw-bold px-3 py-2 rounded-pill";

            case "canceled":
            case "cancelled":
                return "badge badge-light-danger text-danger fw-bold px-3 py-2 rounded-pill";

            case "rescheduled":
                return "badge badge-light-warning text-warning fw-bold px-3 py-2 rounded-pill";

            default:
                return "badge badge-light-dark text-dark fw-bold px-3 py-2 rounded-pill";
        }
    };

    console.log(doctorDashboardCounts)

    return (
        <>
            <DoctorLayout>
                <div className="row g-4 mb-4">
                    <div className="col-12 col-xl-12">
                        <div className="row g-4">
                            {topStats.slice(0, 4).map((item) => (
                                <div className="col-12 col-md-3" key={`${item.title}-grid`}>
                                    <div
                                        className="card border-0 text-white rounded-4 shadow-sm overflow-hidden h-100 dashboard-stat-card"
                                        style={{ background: item.gradient, minHeight: "155px" }}
                                    >
                                        <div className="card-body p-4 d-flex align-items-center justify-content-between">
                                            <div className="pe-3">
                                                <div className="text-uppercase fw-bold fs-8 opacity-75 mb-2">
                                                    {item.title}
                                                </div>
                                                <div className="fs-1 fw-bolder mb-1">{item.value}</div>
                                                <div className="fs-7 opacity-75">{item.sub}</div>
                                            </div>

                                            <div className="text-white opacity-50 flex-shrink-0">
                                                <i
                                                    className={item.icon}
                                                    style={{ fontSize: "4.5rem", lineHeight: 1 }}
                                                ></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="card border-0 rounded-4 shadow-sm my-4 dashboard-section-card">
                    <div className="card-body p-3 p-lg-4">
                        <div className="row g-4">
                            <div className="col-12">
                                <div className="card border-0 rounded-4 shadow-sm-sm bg-white">
                                    <div className="card-header bg-white border-0 pt-2 pb-0">
                                        <div className="w-100">
                                            <div className="bg-light-primary text-primary fw-bold px-4 py-3 rounded-3 fs-7 text-uppercase d-flex align-items-center gap-2">
                                                <i className="bi bi-funnel-fill"></i>
                                                <span>Filter Parameters</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card-body p-4">
                                        <form onSubmit={handleSearch}>
                                            <div className="row g-3 align-items-end">
                                                <div className="col-12 col-md-6 col-xl-3">
                                                    <label className="form-label fw-semibold text-muted fs-8 text-uppercase mb-1">
                                                        Patient Name
                                                    </label>

                                                    <div className="position-relative">
                                                        <input
                                                            type="text"
                                                            name="patientName"
                                                            className="form-control form-control-sm rounded-3 pe-10"
                                                            placeholder="Search by Patient Name or Phone"
                                                            value={filters.patientName}
                                                            onChange={handleChange}
                                                        />

                                                        {filters.patientName && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-1 text-muted"
                                                                onClick={() =>
                                                                    setFilters((prev) => ({
                                                                        ...prev,
                                                                        patientName: "",
                                                                    }))
                                                                }
                                                                style={{
                                                                    width: "28px",
                                                                    height: "28px",
                                                                    border: 0,
                                                                    background: "transparent",
                                                                }}
                                                            >
                                                                <i className="bi bi-x-circle-fill"></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-6 col-xl-2">
                                                    <label className="form-label fw-semibold text-muted fs-8 text-uppercase mb-1">
                                                        From Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        name="fromDate"
                                                        className="form-control form-control-sm rounded-3"
                                                        value={filters.fromDate}
                                                        onChange={handleChange}
                                                    />
                                                </div>
                                                <div className="col-12 col-md-6 col-xl-2">
                                                    <label className="form-label fw-semibold text-muted fs-8 text-uppercase mb-1">
                                                        To Date
                                                    </label>
                                                    <input
                                                        type="date"
                                                        name="toDate"
                                                        className="form-control form-control-sm rounded-3"
                                                        value={filters.toDate}
                                                        onChange={handleChange}
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6 col-xl-2">
                                                    <label className="form-label fw-semibold text-muted fs-8 text-uppercase mb-1">
                                                        Status
                                                    </label>
                                                    <select
                                                        name="status"
                                                        className="form-select form-select-sm rounded-3"
                                                        value={filters.status}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">All Status</option>
                                                        <option value="NEW">New</option>
                                                        <option value="BOOKED">Booked</option>
                                                        <option value="CONFIRMED">Confirmed</option>
                                                        <option value="COMPLETED">Completed</option>
                                                        <option value="DRAFT">Draft</option>
                                                        <option value="REJECTED">Rejected</option>
                                                        <option value="CANCELED">Canceled</option>
                                                        <option value="RESCHEDULED">Rescheduled</option>
                                                    </select>
                                                </div>

                                                <div className="col-12 col-md-6 col-xl-2">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary btn-sm px-4 w-100 rounded-3 shadow-sm"
                                                    >
                                                        <i className="bi bi-search me-2"></i>
                                                        Search
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12">
                                <div className="card border-0 rounded-4 shadow-sm bg-white">
                                    <div className="card-body p-4 p-lg-5">
                                        <div className="d-flex align-items-center gap-3 mb-4">
                                            <div
                                                className="rounded-circle bg-light-info d-flex align-items-center justify-content-center shadow-sm"
                                                style={{ width: "50px", height: "50px" }}
                                            >
                                                <i className="bi bi-table text-info fs-4"></i>
                                            </div>
                                            <div>
                                                <h3 className="fw-bold text-dark mb-1">My Appointments</h3>
                                                <div className="text-muted fs-7">Today and upcoming consultations</div>
                                            </div>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table align-middle table-row-dashed mb-0">
                                                <thead>
                                                    <tr className="fw-bold text-dark" style={{ backgroundColor: "#dbe8fb" }}>
                                                        <th className="ps-5">Patient</th>
                                                        <th>Age</th>
                                                        <th>Gender</th>
                                                        <th>Date</th>
                                                        <th>Time</th>
                                                        <th>Status</th>
                                                        <th className="text-center">Action</th>
                                                    </tr>
                                                </thead>

                                                <tbody className="fw-semibold text-gray-700">
                                                    {loading ? (
                                                        <tr>
                                                            <td colSpan="7" className="p-0">
                                                                <PremiumLoader
                                                                    text="Loading appointments"
                                                                    subText="Fetching today's consultation list"
                                                                    height="260px"
                                                                />
                                                            </td>
                                                        </tr>
                                                    ) : appointments.length > 0 ? (
                                                        appointments.map((item, index) => (
                                                            <tr key={index}>
                                                                <td className="ps-5 fw-bold text-dark">{item.patient}</td>
                                                                <td>{item.age}</td>
                                                                <td>{item.gender}</td>
                                                                <td>{item.date}</td>
                                                                <td>{item.time}</td>
                                                                <td>
                                                                    <span className={getStatusBadge(item.status)}>
                                                                        {item.status}
                                                                    </span>
                                                                </td>
                                                                <td className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-primary btn-sm px-3 rounded-3 shadow-sm"
                                                                        onClick={() => navigate(`/doctor/consultation/${item.AppointmentId}`, { state: item })}
                                                                    >
                                                                        <i className="bi bi-eye me-1"></i>
                                                                        Details
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="7" className="text-center py-10 text-muted fw-semibold">
                                                                No appointments found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>

                                            <Pagination
                                                currentPage={currentPage}
                                                totalCount={totalCount}
                                                pageSize={pageSize}
                                                onPageChange={handlePageChange}
                                                onPageSizeChange={handlePageSizeChange}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </DoctorLayout>

            <style>
                {`
            .dashboard-stat-card,
            .dashboard-panel-card,
            .dashboard-section-card,
            .schedule-item-card {
                transition: all 0.25s ease;
            }

            .dashboard-stat-card:hover {
                transform: translateY(-4px);
                box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.12) !important;
            }

            .dashboard-panel-card:hover,
            .dashboard-section-card:hover {
                box-shadow: 0 1rem 2rem rgba(0, 0, 0, 0.08) !important;
            }

            .schedule-item-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.08);
                border-color: #dbe8fb !important;
            }import Pagination from './../../Components/Common/Pagination/Pagination';


            .table tbody tr {
                transition: background-color 0.2s ease;
            }

            .table tbody tr:hover {
                background-color: #f8fbff;
            }
        `}
            </style>
        </>

    );
}
